import styled from '@emotion/styled';
import { useState, useCallback, useEffect } from 'react';
import { Heading, Box, SimpleGrid, Button, Text, useDisclosure } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import { FormattedMessage } from 'react-intl';
import { isSubmitDisabled } from '../../../../services/utils';
import { enUSFormatExcludingTime } from '../../../../services/items';
import { DEFAULTFLATWEIGHT, DEFAULTWINDSIM } from 'constants/renewables';
import { ReactComponent as AddIcon } from '../../../../icons/add-item.svg';

import useItemCreationWizard from 'hooks/useItemCreationWizard';
import SecondaryButton from '../../../utils/SecondaryButton';
import AutomaticFormikDirtyCheck from '../../../forms/AutomaticFormikDirtyCheck';
import InputFormikField from 'components/forms/InputFormikField';
import ComboboxFormikField from 'components/forms/ComboboxFormikField';
import DateFormikField from 'components/forms/DateFormikField';
import AddWeatherZoneModal from 'components/itemCreationWizard/CustomSubItemCreation/WeatherZone/AddWeatherZoneModal';

const StartNewSubItem = ({ renewableDefinitions, formLabels, onStartSubItemSubmit, validateDescription }) => {
    const [isStartingNewSubItem, setIsStartingNewSubItem] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const createWeatherZoneModal = useDisclosure();

    const { availableWeatherZones, updateAvailableWeatherZones } = useItemCreationWizard();

    useEffect(() => {
        if (!Array.isArray(availableWeatherZones) || availableWeatherZones.length === 0) {
            updateAvailableWeatherZones();
        }
    }, [availableWeatherZones, updateAvailableWeatherZones]);

    const initialStartNewSubItemFormValues = {
        description: '',
        weatherZoneId: '',
        moduleProcessId: DEFAULTWINDSIM, // This is default id for Default Wind Sim
        modelWeightId: DEFAULTFLATWEIGHT, // This is default id for Flat Weight
        plantTypeId: null,
        deliverabilityStatusId: null,
        pcdsPercent: null,
        onlineDate: null,
        offlineDate: null,
    };

    const openCreateSubItemForm = useCallback(() => {
        setIsStartingNewSubItem(true);
    }, []);

    const closeCreateSubItemForm = useCallback(() => {
        setIsStartingNewSubItem(false);
    }, []);

    const onSubmit = useCallback(
        async (values) => {
            await onStartSubItemSubmit(values);
            setIsStartingNewSubItem(false);
        },
        [onStartSubItemSubmit]
    );

    return (
        <CenteredWrapper>
            <Heading pt={7} pb={5}>
                <FormattedMessage
                    id="item_creation_wizard_custom_sub_item_heading"
                    values={{ itemType: 'Renewable' }}
                />
            </Heading>
            {!isStartingNewSubItem && (
                <SimpleGrid columns={{ xl: 6 }} spacingX={6} spacingY={4} mb={4}>
                    <Button variant="secondary" onClick={openCreateSubItemForm}>
                        <Box as="span" textTransform="capitalize">
                            <FormattedMessage id="item_creation_wizard_start_new_subitem" />
                        </Box>
                    </Button>
                </SimpleGrid>
            )}
            {isStartingNewSubItem && (
                <>
                    <Formik enableReinitialize onSubmit={onSubmit} initialValues={initialStartNewSubItemFormValues}>
                        {({ isSubmitting, errors, touched, values }) => (
                            <Form>
                                <>
                                    <AutomaticFormikDirtyCheck
                                        isFormDirty={isFormDirty}
                                        onFormDirtyChange={setIsFormDirty}
                                    />
                                    <Box mb={4}>
                                        <SimpleGrid columns={{ xl: 2 }} spacingX={6} spacingY={4} mb={4}>
                                            <InputFormikField
                                                id="description"
                                                name="description"
                                                type="text"
                                                label={<FormattedMessage id="renewable_definitions_name" />}
                                                isRequired
                                                validate={(value) => validateDescription(value)}
                                            />
                                            <ComboboxFormikField
                                                id="moduleProcessId"
                                                name="moduleProcessId"
                                                label={formLabels.modelType}
                                                isRequired
                                                options={renewableDefinitions?.modelTypes}
                                                valueKey="id"
                                                labelKey="labelName"
                                                placeholderLabel={formLabels.selectPlaceholder}
                                            />
                                            <SimpleGrid columns={{ xl: 2 }} spacingX={6} spacingY={4}>
                                                <ComboboxFormikField
                                                    id="weatherZoneId"
                                                    name="weatherZoneId"
                                                    label={formLabels.weatherZone}
                                                    isRequired
                                                    options={availableWeatherZones}
                                                    labelKey="name"
                                                    valueKey="id"
                                                    placeholderValue=""
                                                    placeholderLabel={formLabels.selectPlaceholder}
                                                    width="100%"
                                                />
                                                <Box>
                                                    <Text size="xs" letterSpacing="0.5px" mb={1}>
                                                        <FormattedMessage id="item_creation_wizard_weatherZone_heading" />
                                                    </Text>
                                                    <SecondaryButton
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={createWeatherZoneModal.onOpen}
                                                        width="50%"
                                                        leftIcon={<AddIcon />}
                                                    >
                                                        <Box as="span" textTransform="capitalize">
                                                            <FormattedMessage id="common_add_new" />
                                                        </Box>
                                                    </SecondaryButton>
                                                </Box>
                                            </SimpleGrid>

                                            {Array.isArray(renewableDefinitions?.plantTypes) &&
                                                renewableDefinitions?.plantTypes.length > 0 && (
                                                    <ComboboxFormikField
                                                        id="plantTypeId"
                                                        name="plantTypeId"
                                                        label={formLabels.plantType}
                                                        options={renewableDefinitions?.plantTypes}
                                                        valueKey="id"
                                                        labelKey="description"
                                                        placeholderLabel="-"
                                                        placeholderValue={null}
                                                        showPlaceholder={true}
                                                    />
                                                )}

                                            <DateFormikField
                                                id="onlineDate"
                                                name="onlineDate"
                                                label={formLabels.onlineDate}
                                                isRequired={false}
                                                showTimeInput={false}
                                                isDisabled={false}
                                                utc={false}
                                                dateFormat={enUSFormatExcludingTime}
                                                isInPortal={true}
                                                info={
                                                    <FormattedMessage id="item_creation_wizard_renewables_online_date" />
                                                }
                                            />

                                            <DateFormikField
                                                id="offlineDate"
                                                name="offlineDate"
                                                label={formLabels.offlineDate}
                                                isRequired={false}
                                                showTimeInput={false}
                                                isDisabled={false}
                                                utc={false}
                                                dateFormat={enUSFormatExcludingTime}
                                                isInPortal={true}
                                                info={
                                                    <FormattedMessage id="item_creation_wizard_renewables_offline_date" />
                                                }
                                            />

                                            <ComboboxFormikField
                                                id="modelWeightId"
                                                name="modelWeightId"
                                                label={formLabels.dataWeight}
                                                options={renewableDefinitions?.dataWeightings.filter(
                                                    (x) => x.id === DEFAULTFLATWEIGHT
                                                )}
                                                valueKey="id"
                                                labelKey="description"
                                                placeholderValue=""
                                                placeholderLabel={formLabels.selectPlaceholder}
                                                isDisabled={true}
                                            />
                                        </SimpleGrid>
                                    </Box>
                                    <Box ml="auto" mb={4} justifyContent="stretch" flexWrap="wrap">
                                        <Button variant="secondary" onClick={closeCreateSubItemForm}>
                                            <Box as="span" textTransform="capitalize">
                                                <FormattedMessage id="common_cancel" />
                                            </Box>
                                        </Button>

                                        <Button
                                            type="submit"
                                            isDisabled={isSubmitDisabled({
                                                errors,
                                                touched,
                                            })}
                                            isLoading={isSubmitting}
                                            ml={3}
                                        >
                                            <FormattedMessage id="common_item_create_btn" />
                                        </Button>
                                    </Box>
                                </>
                            </Form>
                        )}
                    </Formik>

                    {createWeatherZoneModal.isOpen && (
                        <AddWeatherZoneModal
                            isOpen
                            weatherZones={renewableDefinitions?.weatherZones}
                            onClose={createWeatherZoneModal.onClose}
                        />
                    )}
                </>
            )}
        </CenteredWrapper>
    );
};

const CenteredWrapper = styled(Box)`
    width: 100%;
    max-width: 1314px;
    margin: 0 auto;
    padding: 0 16px;
`;

export default StartNewSubItem;
